import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Screen } from '@aibify/display/client';
import { api, ApiError } from '../lib/api.ts';
import { roleAtLeast, useSession } from '../lib/auth.ts';
import { TERMINAL, type LatestRunResponse, type RunDetail } from '../lib/contract.ts';
import { lookupSummary, type Values } from '../lib/schemaForm.ts';
import { useIsDesktop } from '../lib/media.ts';
import { T } from '../lib/strings.ts';
import { EmptyState, ErrorView, Loading } from '../components/Status.tsx';
import { ParamsForm, paramsSummary } from '../components/ParamsForm.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card } from '../components/ui/card.tsx';
import { fmtWhen } from '../lib/time.ts';
import { cn } from '../lib/utils.ts';
import { Badge, StatusLabel, runTone } from '../components/ui/badge.tsx';
import {
  IconAlert,
  IconArrowBack,
  IconChart,
  IconChevronForward,
  IconClock,
  IconFile,
  IconHistory,
  IconLayers,
  IconPlay,
  IconPlug,
  IconSliders,
  IconSparkles,
  IconUser,
  IconUsers,
} from '../components/brand/icons.tsx';
import { InterfacesTab, useInterfacesTab } from '../components/ArtifactInterfaces.tsx';
import { AskPanel } from '../components/AskPanel.tsx';
import { TriggersTab } from '../components/ArtifactTriggers.tsx';
import { MailConnectCard } from '../components/MailConnectDrawer.tsx';
import { useMailConnect, useOnMailConnected } from '../lib/mailConnect.ts';
import { ShareChip, ShareDrawer } from '../components/ShareDrawer.tsx';
import { kindLabel, SpecTab, UpgradeButton } from '../components/ArtifactKind.tsx';
import { ArtifactActions } from '../components/ArtifactActions.tsx';
import { CacheBar, isCacheable } from '../components/CacheBar.tsx';
import { RestrictedNote } from '../components/DataAccess.tsx';
import { ScreenExpand } from '../components/ScreenExpand.tsx';
import { DraftBanner } from '../components/DraftBanner.tsx';
import { IconCheck, IconX } from '../components/brand/icons.tsx';

/** שאלות לדוגמה ביצירת chat (כפתורים לפני השאלה הראשונה). */
const ASK_SUGGESTIONS = ['מי הסוכן הכי חזק השנה?', 'כמה מכרנו בספטמבר?', 'מכירות לפי חודש השנה'];

const POLL_MS = 500;

/** ריצה עד מצב סופי (שלד: polling על GET /api/runs/:id; SSE של 09 — באבן הדרך "מלא"). */
function useRun(runId: string | null) {
  return useQuery({
    queryKey: ['run', runId],
    queryFn: () => api.getRun(runId!),
    enabled: !!runId,
    refetchInterval: (q) => (q.state.data && TERMINAL.has(q.state.data.status) ? false : POLL_MS),
  });
}

/** artifacts-draft-preview: ?version=<n> — תצוגה מקדימה של גרסה שאינה החיה (היוצר בלבד; השרת מחזיר 404 לאחר). */
export function previewVersionOf(v: string | null): number | undefined {
  return v && /^[1-9][0-9]{0,6}$/.test(v) ? Number(v) : undefined;
}

/** מצב המסך (ריצה, מטמון, טופס) מתאפס במעבר בין הגרסה החיה לתצוגה מקדימה של טיוטה. */
export function ArtifactPage() {
  const { slug = '' } = useParams();
  const [sp] = useSearchParams();
  const version = previewVersionOf(sp.get('version'));
  return <ArtifactScreen key={`${slug}:${version ?? 'live'}`} slug={slug} version={version} />;
}

function ArtifactScreen({ slug, version }: { slug: string; version: number | undefined }) {
  const s = useSession();
  const qc = useQueryClient();
  const nav = useNavigate();
  const loc = useLocation();
  const art = useQuery({ queryKey: ['artifact', slug, version ?? null], queryFn: () => api.getArtifact(slug, version) });
  // אחרי "פרסם" בתצוגה המקדימה — חזרה לגרסה החיה עם הודעה
  const [notice, setNotice] = useState<string | null>(() => ((loc.state as { notice?: unknown } | null)?.notice as string | undefined) ?? null);
  useEffect(() => {
    if ((loc.state as { notice?: unknown } | null)?.notice) void nav(loc.pathname + loc.search, { replace: true, state: null });
  }, [loc.state, loc.pathname, loc.search, nav]);
  const [runId, setRunId] = useState<string | null>(null);
  const [submitErr, setSubmitErr] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastValues, setLastValues] = useState<Values | null>(null);
  const [lastParams, setLastParams] = useState<Record<string, unknown> | null>(null);
  const lastLabels = useRef<Record<string, string> | undefined>(undefined);
  const [formOpen, setFormOpen] = useState(true);
  // artifacts-report-lookup: אחרי הרצה — בדסקטופ (≥768px) הטופס נשאר פתוח; במובייל מתקפל לשורת סיכום שכולה כפתור
  const desktop = useIsDesktop();
  // artifacts-report-cache (report@2): הריצה השמורה במפתח (פרמטרים + זהות) — מוצגת מיד; ריצה חדשה (runId) מחליפה אותה כשהיא done
  const [cached, setCached] = useState<LatestRunResponse | null>(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  const cacheChecked = useRef<string | null>(null);
  // 09 §1.1: מגירת "שיתוף" — רק ליוצר (canEdit)
  const [shareOpen, setShareOpen] = useState(false);
  const run = useRun(runId);
  const ifaces = useInterfacesTab(slug, s?.user.role);
  // הלשונית ב-URL (?tab=) — קישור "ללשונית ממשקים" מאזהרת התזמון (19 §1.5)
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') ?? 'run';
  const setTab = (v: string) =>
    setSp(
      (p) => {
        const n = new URLSearchParams(p);
        if (v === 'run') n.delete('tab');
        else n.set('tab', v);
        return n;
      },
      { replace: true },
    );
  const done = !!run.data && TERMINAL.has(run.data.status);
  const busy = submitting || (!!runId && !done && !run.isError);

  useEffect(() => {
    if (done) void qc.invalidateQueries({ queryKey: ['runs', slug] });
  }, [done, qc, slug]);

  // 20: הריצה נעצרה כי אין Gmail — "חיבור Gmail" נפתח מעצמו (פעם אחת לריצה); אחרי החיבור — אותה ריצה שוב, אותם פרמטרים
  const mailCaps = done ? run.data?.mailConnect?.caps : undefined;
  const openMail = useMailConnect();
  const mailOpened = useRef<string | null>(null);
  useEffect(() => {
    if (!mailCaps || !runId || mailOpened.current === runId) return;
    mailOpened.current = runId;
    openMail(mailCaps);
  }, [mailCaps, runId, openMail]);
  useOnMailConnected(() => {
    if (lastParams && lastValues) void submit(lastParams, lastValues, lastLabels.current);
  }, !!mailCaps && !!lastParams);

  // artifacts-draft-preview: הגרסה המוצגת אינה החיה (ליוצר). chat — "שאל" רץ על החיה, אין תצוגה מקדימה
  const preview = art.data?.preview && art.data.artifact.kind !== 'chat' ? art.data.preview : undefined;
  const pv = preview?.version;
  // פתיחה של report@2: המטמון של פרמטרי ברירת המחדל ({} — השרת משלים ברירות מחדל); אין → כמו היום (טופס + "הרצה"). טיוטה — בלי מטמון
  const cacheable = !!art.data && !preview && isCacheable(art.data.artifact);
  useEffect(() => {
    if (!cacheable || cacheChecked.current === slug) return;
    cacheChecked.current = slug;
    setCacheLoading(true);
    api
      .latestRun(slug, {})
      .then((hit) => {
        if (!hit) return;
        setCached(hit);
        setLastParams((hit.run.params ?? {}) as Record<string, unknown>);
        if (hit.recomputing) setRunId(hit.recomputing);
      })
      .catch(() => {})
      .finally(() => setCacheLoading(false));
  }, [cacheable, slug]);

  if (art.isPending) return <Loading />;
  if (art.isError) return <ErrorView error={art.error} onRetry={() => art.refetch()} />;
  const { artifact: a, versions, paramsSchema, ask, canEdit, spec, build } = art.data;
  // 22 §1.7 + web-artifacts-rows-and-versions: מפרט — לכל מי שרואה (גם שותף). בנייה וקבצים לא מוצגים לאף אחד; כישלון בנייה
  // שחוסם פרסום — ליוצר הודעה אחת בלי פרטים (build קיים בתשובה ליוצר בלבד)
  const buildFailed = !!canEdit && a.kind === 'app' && build?.ok === false;
  const isChat = a.kind === 'chat';
  const runAsHe = a.runAs === 'creator' ? a.creator.nameHe : null;

  const submit = async (params: Record<string, unknown>, values: Values, paramLabels?: Record<string, string>) => {
    setSubmitErr(null);
    setSubmitting(true);
    lastLabels.current = paramLabels;
    try {
      // report@2: שינוי פרמטרים = מפתח אחר — המטמון שלו אם יש (והשרת מחליט אם לחשב ברקע); אחרת ריצה כמו היום
      if (cacheable) {
        const hit = await api.latestRun(slug, params).catch(() => null);
        if (hit) {
          setCached(hit);
          setLastValues(values);
          setLastParams(params);
          setRunId(hit.recomputing);
          setFormOpen(desktop);
          return;
        }
        setCached(null);
      }
      const r = await api.createRun(slug, params, paramLabels, pv);
      setLastValues(values);
      setLastParams(params);
      setRunId(r.runId);
      setFormOpen(desktop);
    } catch (e) {
      setSubmitErr(e);
    } finally {
      setSubmitting(false);
    }
  };

  const TypeIcon = KIND_ICON[a.kind] ?? IconFile;

  const liveShown = done && !!run.data && (run.data.status === 'done' || !!run.data.output);
  const shownRun: RunDetail | LatestRunResponse['run'] | null = liveShown ? run.data! : cached ? cached.run : null;
  const recomputingNow = !!runId && !done && !run.isError && !!cached;

  /** "חשב מחדש": ריצה חדשה תמיד (גם לדוח עם פעולה חיצונית); מה שמוצג עכשיו נשאר עד שהיא מסתיימת. */
  const recompute = async () => {
    setSubmitErr(null);
    setSubmitting(true);
    try {
      if (liveShown && run.data) setCached({ run: run.data, stale: false, newDataSince: null, noNewData: false, recomputing: null, ttlMin: cached?.ttlMin ?? 60, external: cached?.external ?? false });
      const r = lastParams
        ? await api.recompute(slug, lastParams, lastLabels.current)
        : await api.recompute(slug, (cached?.run.params as Record<string, unknown> | undefined) ?? {}, cached?.run.paramLabels ?? undefined);
      setRunId(r.runId);
    } catch (e) {
      setSubmitErr(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="flex flex-col gap-5">
      <nav aria-label="breadcrumb" className="-mb-2">
        <Link to="/artifacts" className="inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary">
          <IconArrowBack size={16} />
          {T.artifacts.title}
        </Link>
      </nav>
      <header className="flex items-start gap-4">
        <span aria-hidden="true" className="hidden size-14 shrink-0 place-items-center rounded-md bg-primary-soft text-primary sm:grid">
          <TypeIcon size={28} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <h1 className="page-title">{a.nameHe}</h1>
          <p className="m-0 max-w-3xl text-muted-foreground">{a.descriptionHe}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {a.currentVersion != null && (
              <Badge tone="neutral" className="font-semibold" data-testid="artifact-version">
                {T.artifact.version(a.currentVersion)}
              </Badge>
            )}
            {/* שני צירי גרסה: גרסת היצירה (למעלה) ובנפרד, מעומעם — הסוג וגרסתו */}
            <span className="text-sm text-muted-foreground" data-testid="artifact-kind">
              {kindLabel(a)}
            </span>
            {canEdit && <UpgradeButton a={a} />}
            {runAsHe && (
              <Badge tone="info" className="gap-1" data-testid="artifact-run-as">
                <IconUser size={14} />
                רץ בשם {runAsHe}
              </Badge>
            )}
            <ShareChip shared={a.shared} sharedBy={a.sharedBy} />
          </div>
          {buildFailed && (
            <p role="status" className="m-0 mt-1 flex w-fit items-center gap-2 rounded-md border border-degraded/40 bg-degraded/[0.08] px-3 py-2 text-sm font-medium" data-testid="build-failed-notice">
              <IconAlert size={16} className="shrink-0 text-degraded" />
              {T.kinds.buildFailedShort}
            </p>
          )}
        </div>
        {canEdit && (
          <Button variant="outline" className="shrink-0 gap-1.5" onClick={() => setShareOpen(true)} data-testid="share-btn">
            <IconUsers size={18} />
            {T.share.button}
          </Button>
        )}
        {canEdit && <ArtifactActions slug={a.slug} nameHe={a.nameHe} descriptionHe={a.descriptionHe ?? ''} />}
      </header>
      {notice && (
        <div role="status" className="flex items-center gap-3 rounded-md border border-ok/40 bg-card p-3 shadow-card" data-testid="publish-notice">
          <IconCheck size={18} className="shrink-0 text-ok" />
          <span className="me-auto text-sm">{notice}</span>
          <button type="button" className="grid size-9 shrink-0 cursor-pointer place-items-center rounded-sm text-muted-foreground hover:bg-muted" aria-label={T.artifacts.dismiss} onClick={() => setNotice(null)}>
            <IconX size={16} />
          </button>
        </div>
      )}
      {preview && canEdit && (
        <DraftBanner
          slug={a.slug}
          version={preview.version}
          draft={preview.draft}
          live={a.currentVersion}
          onPublished={(v) => {
            void qc.invalidateQueries({ queryKey: ['artifact', slug] });
            void qc.invalidateQueries({ queryKey: ['artifacts'] });
            nav(`/artifacts/${encodeURIComponent(slug)}`, { replace: true, state: { notice: T.artifact.published(v) } });
          }}
        />
      )}
      {canEdit && <ShareDrawer open={shareOpen} onClose={() => setShareOpen(false)} slug={a.slug} nameHe={a.nameHe} minRole={a.minRole} published={a.currentVersion != null} />}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="run">
            <IconPlay size={16} className="max-sm:hidden" />
            {T.artifact.tabRun}
          </TabsTrigger>
          <TabsTrigger value="runs">
            <IconHistory size={16} className="max-sm:hidden" />
            {T.artifact.tabRuns}
          </TabsTrigger>
          {roleAtLeast(s?.user.role, 'creator') && (
            <TabsTrigger value="versions">
              <IconLayers size={16} className="max-sm:hidden" />
              {T.artifact.tabVersions}
            </TabsTrigger>
          )}
          {roleAtLeast(s?.user.role, 'creator') && !isChat && (
            <TabsTrigger value="triggers" data-testid="tab-triggers">
              <IconClock size={16} className="max-sm:hidden" />
              {T.triggers.tab}
            </TabsTrigger>
          )}
          {spec && (
            <TabsTrigger value="spec" data-testid="tab-spec">
              <IconFile size={16} className="max-sm:hidden" />
              {T.kinds.tabSpec}
            </TabsTrigger>
          )}
          {ifaces.visible && (
            <TabsTrigger value="interfaces" data-testid="tab-interfaces">
              <IconPlug size={16} className="max-sm:hidden" />
              {T.iface.tab}
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="run" className="flex flex-col gap-4">
          {isChat ? (
            <AskPanel slug={a.slug} titleHe={a.nameHe} variant="page" suggestions={ASK_SUGGESTIONS} />
          ) : (
          <>
          <Card className="p-4 md:p-5">
            {formOpen || desktop || !lastValues ? (
              <ParamsForm schema={paramsSchema} busy={busy} onSubmit={submit} initial={lastValues ?? undefined} lookup={(param, q) => api.lookup(slug, param, q, pv)} />
            ) : (
              // מובייל: כל השורה היא כפתור שפותח את הטופס עם הערכים האחרונים
              <button
                type="button"
                onClick={() => setFormOpen(true)}
                className="flex min-h-11 w-full cursor-pointer items-center gap-3 rounded-md text-start focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25"
                aria-label={`${T.artifact.changeParams}: ${paramsSummary(paramsSchema, lastValues) || T.artifact.params}`}
                data-testid="params-summary"
              >
                <span aria-hidden="true" className="grid size-9 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
                  <IconSliders size={18} />
                </span>
                <span className="me-auto min-w-0 text-sm text-muted-foreground">{paramsSummary(paramsSchema, lastValues) || T.artifact.params}</span>
                <span className="shrink-0 text-sm font-semibold text-primary">{T.artifact.changeParams}</span>
              </button>
            )}
          </Card>
          {submitErr != null && <ErrorView error={submitErr} />}
          {run.isError && <ErrorView error={run.error} />}
          {!runId && !submitting && submitErr == null && !cached && !cacheLoading && (
            <EmptyState icon={<IconPlay size={22} />} title={T.artifact.readyTitle}>
              {T.artifact.readyHint}
            </EmptyState>
          )}
          {cacheLoading && !cached && !runId && <Loading variant="screen" />}
          {runId && run.data && !done && !cached && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3 rounded-md border border-laser/30 bg-primary-soft/60 p-3" role="status" data-testid="run-status">
                <span aria-hidden="true" className="relative flex size-3 shrink-0">
                  <span className="absolute inline-flex size-full animate-ping rounded-sm bg-laser opacity-60" />
                  <span className="relative inline-flex size-3 rounded-circle bg-laser" />
                </span>
                <span className="me-auto font-semibold">{T.artifact.status[run.data.status] ?? run.data.status}…</span>
                <Button variant="ghost" size="sm" onClick={() => void api.cancelRun(runId).catch(() => {})}>
                  {T.artifact.cancel}
                </Button>
              </div>
              <Loading variant="screen" />
            </div>
          )}
          {cacheable && shownRun && (
            <CacheBar
              finishedAt={shownRun.finishedAt}
              busy={busy && !recomputingNow}
              recomputing={recomputingNow}
              note={!liveShown && cached ? (cached.noNewData ? T.artifact.noNewData : cached.external && cached.stale ? T.artifact.externalOnly : null) : null}
              onRecompute={() => void recompute()}
            />
          )}
          {done && run.data && !liveShown && (
            <>
              <ErrorView error={new ApiError(0, run.data.status, run.data.errorHe ?? T.artifact.failed)} />
              {mailCaps && <MailConnectCard caps={mailCaps} />}
            </>
          )}
          {liveShown && run.data!.status !== 'done' && <ErrorView error={new ApiError(0, run.data!.status, run.data!.errorHe ?? T.artifact.status[run.data!.status] ?? T.artifact.failed)} />}
          {shownRun && <RestrictedNote />}
          {shownRun && (
            <ScreenExpand labelHe={a.nameHe}>
              <Screen screen={shownRun.output} asOf={shownRun.asOf} fetchFile={api.fetchFile} exportedBy={s?.user.nameHe} />
            </ScreenExpand>
          )}
          {ask && !preview && <AskPanel slug={ask.slug} titleHe={ask.nameHe} />}
          </>
          )}
        </TabsContent>
        <TabsContent value="runs">
          <RunsList slug={slug} paramsSchema={paramsSchema} />
        </TabsContent>
        <TabsContent value="versions">
          <ol className="m-0 flex list-none flex-col p-0" data-testid="versions-list">
            {versions.map((v, i) => {
              const cur = v.version === a.currentVersion;
              // הסוג וגרסת הסוג נעוצים לגרסה (22 §1.6) — מסומן כשהשתנה מהגרסה הקודמת (versions מהחדשה לישנה)
              const prev = versions[i + 1];
              const kindHe = v.kind ? (T.artifacts.kinds[v.kind] ?? v.kind) : null;
              const changed = !!prev && !!v.kindVersion && !!prev.kindVersion && (prev.kindVersion !== v.kindVersion || prev.kind !== v.kind);
              return (
                <li key={v.version} className="relative flex gap-4 pb-4 ps-1 last:pb-0">
                  <span aria-hidden="true" className="absolute start-[13px] top-7 bottom-0 w-px bg-border" />
                  <span aria-hidden="true" className={cn('relative z-[1] mt-1 grid size-6 shrink-0 place-items-center rounded-circle border-2', cur ? 'border-ok bg-ok/15' : 'border-border bg-card')}>
                    <span className={cn('size-2 rounded-circle', cur ? 'bg-ok' : 'bg-muted-foreground/50')} />
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-1 rounded-md border border-border bg-card p-3 shadow-card">
                    <div className="flex flex-wrap items-center gap-2">
                      <strong>{T.artifact.version(v.version)}</strong>
                      {cur && (
                        <StatusLabel tone="ok">
                          {T.artifact.current}
                        </StatusLabel>
                      )}
                      {canEdit && (a.currentVersion == null || v.version > a.currentVersion) && (
                        <StatusLabel tone="warn" data-testid={`version-draft-${v.version}`}>
                          {T.artifact.draft}
                        </StatusLabel>
                      )}
                      {/* artifacts-draft-preview: טיוטה — "לנסות" פותח אותה במסך עם הטופס האמיתי (היוצר בלבד; לא chat) */}
                      {canEdit && !isChat && (a.currentVersion == null || v.version > a.currentVersion) && v.version !== pv && (
                        <Link to={`/artifacts/${encodeURIComponent(slug)}?version=${v.version}`} className="ms-auto inline-flex min-h-11 items-center rounded-sm px-3 text-sm font-medium text-primary hover:bg-primary-soft" data-testid={`version-try-${v.version}`}>
                          {T.artifact.tryDraft}
                        </Link>
                      )}
                      {kindHe && v.kindVersion != null && (
                        <Badge tone={changed ? 'info' : 'neutral'} className={cn('gap-1', changed && 'font-semibold')} data-testid={`version-kind-${v.version}`} data-changed={changed ? 'true' : undefined}>
                          {changed && <IconSparkles size={13} />}
                          {T.kinds.label(kindHe, v.kindVersion, true)}
                          {changed && <span className="sr-only"> · {T.kinds.kindChanged}</span>}
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {v.noteHe} · {v.createdBy.nameHe} · <bdi dir="auto">{fmtWhen(v.createdAt)}</bdi>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </TabsContent>
        {roleAtLeast(s?.user.role, 'creator') && !isChat && (
          <TabsContent value="triggers">
            <TriggersTab slug={slug} artifactNameHe={a.nameHe} paramsSchema={paramsSchema} interfacesHref={ifaces.visible ? `/artifacts/${slug}?tab=interfaces` : null} />
          </TabsContent>
        )}
        {spec && (
          <TabsContent value="spec">
            <SpecTab spec={spec} />
          </TabsContent>
        )}
        {ifaces.visible && (
          <TabsContent value="interfaces">
            <InterfacesTab slug={slug} query={ifaces.query} />
          </TabsContent>
        )}
      </Tabs>
    </section>
  );
}

function RunsList({ slug, paramsSchema }: { slug: string; paramsSchema: unknown }) {
  const q = useQuery({ queryKey: ['runs', slug], queryFn: () => api.listRuns(slug) });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorView error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data.runs.length) return <EmptyState icon={<IconHistory size={22} />} title={T.artifact.noRuns} />;
  return (
    <ul className="m-0 flex list-none flex-col divide-y divide-border overflow-hidden rounded-md border border-border bg-card p-0 shadow-card" data-testid="runs-list">
      {q.data.runs.map((r) => (
        <li key={r.id}>
          <Link
            to={`/artifacts/${slug}/runs/${r.id}`}
            className="group flex min-h-14 flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 transition-colors hover:bg-muted"
          >
            <StatusLabel tone={runTone(r.status)}>
              {T.artifact.status[r.status] ?? r.status}
            </StatusLabel>
            <bdi dir="auto" className="text-sm font-medium tabular-nums">
              {r.startedAt ? fmtWhen(r.startedAt) : '–'}
            </bdi>
            <span className="text-sm text-muted-foreground">{T.artifact.runBy(r.user.nameHe)}</span>
            {/* artifacts-report-lookup: "לקוח: <תווית>" (ריצה בלי תווית — הערך) */}
            {lookupSummary(paramsSchema, r.params, r.paramLabels) && (
              <bdi dir="auto" className="text-sm font-medium" data-testid="run-params">
                {lookupSummary(paramsSchema, r.params, r.paramLabels)}
              </bdi>
            )}
            {r.trigger && (
              <Badge tone="info" className="gap-1" data-testid="run-from-trigger" title={T.triggers.firedBy(r.trigger.nameHe)}>
                <IconClock size={13} />
                {T.triggers.fromTrigger}
                {r.trigger.attempt === 2 && <span data-testid="run-second-attempt"> · {T.triggers.secondAttempt}</span>}
              </Badge>
            )}
            <span className="text-sm text-muted-foreground">{T.artifact.version(r.version)}</span>
            <IconChevronForward size={18} className="ms-auto text-muted-foreground group-hover:text-primary" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

const KIND_ICON: Record<string, typeof IconFile> = { report: IconChart, chat: IconSparkles, app: IconLayers };
